










































































































































/**
 * NOTE: When using the sync modifier, objects must always be passed with v-bind!
 *  ALl custom validations should evaluate to true to submit form. Invalid = false
 */
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  ChairOrderRecord,
  Order,
  OrderDetails,
  UmbrellaOrderRecord,
} from "@/models/order/order";
import OrderService from "@/services/order_service";
import { GenericError } from "@/services/error_service";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import {
  CustomChairOrder,
  CustomUmbrellaOrder,
} from "@/models/configurator/custom_umbrella";
import { ProductType } from "@/models/products/product";
import { Mobile } from "@/mixins/mobile";
import OrderSummary from "@/components/checkout/OrderSummary.vue";
import { PaymentMethod, PaymentMethodText } from "@/models/order/helpers";

@Component({
  components: {
    OrderSummary,
  },
})
export default class OrderReview extends Mixins(Mobile) {
  @(namespace(StoreModule.Order).Action(OrderActions.AddShipping))
  addShipping!: (value: OrderDetails["shipping_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddBilling))
  addBilling!: (value: OrderDetails["billing_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddPayment))
  addPayment!: (value: OrderDetails["payment_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddContact))
  addContact!: (value: OrderDetails["contact_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddOrderId))
  addOrderId!: (value: { id: string; creator: string }) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  protected loader: any = null;
  protected orderService = new OrderService();
  protected error = {
    billing: false,
    shipping: false,
    payment: false,
    contact: false,
  };
  @Prop() props!: any;

  created() {
    if (!this.props || !this.props.order) {
      this.$router.push("/resubmit?source=review");
    }
    window.scroll(0,0);
  }

  protected get getPaymentMethodText(): string {
    let methodText = "";
        switch (this.props.order.payment_details.method) {
            case PaymentMethod.BillAccount:
                methodText = PaymentMethodText.BillAccount;
                break;
            case PaymentMethod.CreditCard:
                methodText = PaymentMethodText.CreditCard;
                break;
            case PaymentMethod.FullPrepay:
                methodText = PaymentMethodText.FullPrepay;
                break;
            case PaymentMethod.DepositPrepay:
                methodText = PaymentMethodText.DepositPrepay;
                break;
            case PaymentMethod.DepositNet30:
                methodText = PaymentMethodText.DepositNet30;
                break;
            case PaymentMethod.Net15:
                methodText = PaymentMethodText.Net15;
                break;
            case PaymentMethod.Net30:
                methodText = PaymentMethodText.Net30;
                break;
            case PaymentMethod.Net60:
                methodText = PaymentMethodText.Net60;
                break;
            case PaymentMethod.Net90:
                methodText = PaymentMethodText.Net90;
                break;
            case PaymentMethod.EarlyBuyRepayment:
                methodText = PaymentMethodText.EarlyBuyRepayment;
                break;
            case PaymentMethod.ContainerRepayment:
                methodText = PaymentMethodText.ContainerRepayment;
                break;
        }
        return methodText;
  }

  protected async checkout(): Promise<void> {
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement(
          "h1",
          "Submitting your request -- please do not refresh the page."
        ),
      }
    );
    await this.saveDataToStore();
    const order = await this.parseItems();
    if (order._id) {
      await this.convertToOrder(order);
    } else {
      await this.createOrder(order);
    }
  }

  protected async saveDataToStore(): Promise<void> {
    await this.addShipping(this.props.order.shipping_details);
    await this.addBilling(this.props.order.billing_details);
    await this.addPayment(this.props.order.payment_details);
    await this.addContact(this.props.order.contact_details);
  }

  /**
   * The API expects only mfr_codes for fabric swatches. We save the entire fabric swatch object on the frontend because the fabric selector needs the extra data to do filtering
   */
  protected async parseItems(): Promise<Order> {
    const items = JSON.parse(JSON.stringify(this.storeOrder.items!));
    for (const item of items) {
      if (item.product_type === ProductType.Umbrella) {
        const parsedData = this.parseFabricFields(
          (item.item_details as CustomUmbrellaOrder)
            .canopy as CustomUmbrellaOrder["canopy"]
        );
        (item.item_details as CustomUmbrellaOrder).canopy = Object.assign(
          {},
          { ...(item.item_details as CustomUmbrellaOrder).canopy },
          { ...parsedData }
        );
      } else if (item.product_type === ProductType.Chair) {
        const parsedData = this.parseFabricFields(
          item.item_details as CustomChairOrder
        );
        (item.item_details as any) = Object.assign(
          {},
          { ...item.item_details },
          { ...parsedData } as ChairOrderRecord
        );
      }
    }
    const order = { ...this.storeOrder };
    order.items = [...items];
    return order;
  }

  /**
   * API is expecting strings for fabrics. parseFabricFields() convert swatch objects to strings if value is not already a string
   */
  protected parseFabricFields(
    fabric: CustomUmbrellaOrder["canopy"] | CustomChairOrder
  ): UmbrellaOrderRecord | ChairOrderRecord {
    const fabrics = [
      "mainFabric",
      "mainTrimFabric",
      "mainTrimFabricInner",
      "ventTopFabric",
      "ventMiddleFabric",
      "ventTrimFabric",
      "valanceFabric",
      "bindingFabric",
      "ribFabric",
    ];
    const parsed = {} as UmbrellaOrderRecord;
    for (const fabricKey of fabrics) {
      if (fabric[fabricKey] && fabric[fabricKey].mfr_code) {
        parsed[fabricKey] = fabric[fabricKey].mfr_code;
      } else if (
        fabric[fabricKey] &&
        Array.isArray(fabric[fabricKey]) &&
        fabric[fabricKey].length > 0
      ) {
        parsed[fabricKey] = fabric[fabricKey].map((swatch) => {
          if (swatch.mfr_code) {
            return swatch.mfr_code;
          } else {
            return swatch;
          }
        });
      } else {
        parsed[fabricKey] = fabric[fabricKey];
      }
    }
    return parsed;
  }

  protected async createOrder(order: Order): Promise<void> {
    try {
      const res = await this.orderService.createOrder(order);
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }

  protected async convertToOrder(order: Order): Promise<void> {
    try {
      const res = await this.orderService.convertOrderToQuote(
        order._id!,
        order
      );
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }
}
